import { supabase } from '../lib/supabase';

export const aiComplianceService = {
  // ============================================================================
  // PREDICTIVE ANALYTICS - Local Pattern Analysis (No External API)
  // ============================================================================

  /**
   * Analyze historical compliance data to predict future violations
   * Uses local statistical analysis without external AI APIs
   */
  async predictComplianceRisks(filters = {}) {
    try {
      // Fetch historical violations
      const { data: violations, error } = await supabase
        ?.from('compliance_violations')
        ?.select('*')
        ?.order('identified_at', { ascending: false })
        ?.limit(1000);

      if (error) throw error;

      // Analyze patterns
      const riskAnalysis = this.analyzeViolationPatterns(violations);
      const predictions = this.generatePredictions(riskAnalysis);
      const recommendations = this.generateRecommendations(predictions);

      return {
        predictions,
        recommendations,
        confidenceScore: this.calculateConfidenceScore(violations?.length),
        analysisDate: new Date()?.toISOString()
      };
    } catch (error) {
      console.error('Error predicting compliance risks:', error);
      throw error;
    }
  },

  /**
   * Analyze violation patterns to identify trends
   */
  analyzeViolationPatterns(violations) {
    const patterns = {
      byType: {},
      byDepartment: {},
      bySeverity: {},
      byMonth: {},
      recurring: []
    };

    violations?.forEach(violation => {
      // Group by type
      if (!patterns?.byType?.[violation?.violation_type]) {
        patterns.byType[violation?.violation_type] = { count: 0, avgCost: 0, totalCost: 0 };
      }
      patterns.byType[violation?.violation_type].count += 1;
      patterns.byType[violation?.violation_type].totalCost += violation?.estimated_cost || 0;

      // Group by department
      if (violation?.department) {
        if (!patterns?.byDepartment?.[violation?.department]) {
          patterns.byDepartment[violation?.department] = { count: 0, types: new Set() };
        }
        patterns.byDepartment[violation?.department].count += 1;
        patterns?.byDepartment?.[violation?.department]?.types?.add(violation?.violation_type);
      }

      // Group by severity
      if (!patterns?.bySeverity?.[violation?.severity]) {
        patterns.bySeverity[violation?.severity] = 0;
      }
      patterns.bySeverity[violation?.severity] += 1;

      // Group by month
      const month = violation?.identified_at?.substring(0, 7);
      if (!patterns?.byMonth?.[month]) {
        patterns.byMonth[month] = 0;
      }
      patterns.byMonth[month] += 1;
    });

    // Calculate averages
    Object.keys(patterns?.byType)?.forEach(type => {
      const data = patterns?.byType?.[type];
      data.avgCost = data?.count > 0 ? data?.totalCost / data?.count : 0;
    });

    // Identify recurring violations (same type in same department)
    const departmentTypeMap = {};
    violations?.forEach(violation => {
      const key = `${violation?.department}-${violation?.violation_type}`;
      if (!departmentTypeMap?.[key]) {
        departmentTypeMap[key] = [];
      }
      departmentTypeMap?.[key]?.push(violation);
    });

    Object.entries(departmentTypeMap)?.forEach(([key, viols]) => {
      if (viols?.length >= 3) {
        patterns?.recurring?.push({
          key,
          department: viols?.[0]?.department,
          violationType: viols?.[0]?.violation_type,
          occurrences: viols?.length,
          avgTimeBetween: this.calculateAvgTimeBetween(viols)
        });
      }
    });

    return patterns;
  },

  /**
   * Generate predictions based on pattern analysis
   */
  generatePredictions(patterns) {
    const predictions = [];

    // Predict high-risk violation types
    Object.entries(patterns?.byType)
      ?.sort((a, b) => b?.[1]?.count - a?.[1]?.count)
      ?.slice(0, 5)
      ?.forEach(([type, data]) => {
        predictions?.push({
          id: `pred-${predictions?.length + 1}`,
          type: 'violation_risk',
          violationType: type,
          riskLevel: data?.count > 10 ? 'high' : data?.count > 5 ? 'medium' : 'low',
          likelihood: Math.min(95, (data?.count / 50) * 100)?.toFixed(1),
          estimatedCost: data?.avgCost,
          historicalOccurrences: data?.count,
          predictedTimeframe: '30 days'
        });
      });

    // Predict department risks
    Object.entries(patterns?.byDepartment)
      ?.sort((a, b) => b?.[1]?.count - a?.[1]?.count)
      ?.slice(0, 3)
      ?.forEach(([dept, data]) => {
        predictions?.push({
          id: `pred-${predictions?.length + 1}`,
          type: 'department_risk',
          department: dept,
          riskLevel: data?.count > 15 ? 'high' : data?.count > 8 ? 'medium' : 'low',
          likelihood: Math.min(90, (data?.count / 40) * 100)?.toFixed(1),
          violationTypes: Array.from(data?.types),
          historicalOccurrences: data?.count,
          predictedTimeframe: '30 days'
        });
      });

    // Predict recurring violations
    patterns?.recurring?.forEach(recurring => {
      predictions?.push({
        id: `pred-${predictions?.length + 1}`,
        type: 'recurring_pattern',
        department: recurring?.department,
        violationType: recurring?.violationType,
        riskLevel: 'high',
        likelihood: 85,
        pattern: `Recurring every ${recurring?.avgTimeBetween} days`,
        historicalOccurrences: recurring?.occurrences,
        predictedTimeframe: `${recurring?.avgTimeBetween} days`
      });
    });

    return predictions;
  },

  /**
   * Generate automated remediation recommendations
   */
  generateRecommendations(predictions) {
    const recommendations = [];

    predictions?.forEach(prediction => {
      let recommendation = {
        id: `rec-${recommendations?.length + 1}`,
        predictionId: prediction?.id,
        priority: prediction?.riskLevel === 'high' ? 'immediate' : prediction?.riskLevel === 'medium' ? 'high' : 'medium',
        category: prediction?.type,
        title: '',
        description: '',
        actionItems: [],
        estimatedImpact: '',
        implementationTime: ''
      };

      if (prediction?.type === 'violation_risk') {
        recommendation.title = `Prevent ${prediction?.violationType} Violations`;
        recommendation.description = `High risk of ${prediction?.violationType} violations detected. Historical data shows ${prediction?.historicalOccurrences} occurrences.`;
        recommendation.actionItems = [
          'Review current compliance procedures',
          'Conduct targeted training for affected staff',
          'Implement automated validation checks',
          'Schedule monthly compliance audits'
        ];
        recommendation.estimatedImpact = `Reduce violations by 60-80%`;
        recommendation.implementationTime = '2-4 weeks';
      } else if (prediction?.type === 'department_risk') {
        recommendation.title = `${prediction?.department} Department Compliance Review`;
        recommendation.description = `${prediction?.department} shows elevated compliance risk with ${prediction?.historicalOccurrences} violations across ${prediction?.violationTypes?.length} categories.`;
        recommendation.actionItems = [
          'Conduct department-wide compliance assessment',
          'Assign dedicated compliance officer',
          'Implement weekly compliance check-ins',
          'Review and update department procedures'
        ];
        recommendation.estimatedImpact = `Reduce department violations by 50-70%`;
        recommendation.implementationTime = '3-6 weeks';
      } else if (prediction?.type === 'recurring_pattern') {
        recommendation.title = `Break Recurring ${prediction?.violationType} Pattern`;
        recommendation.description = `Recurring pattern detected: ${prediction?.violationType} in ${prediction?.department} every ${prediction?.predictedTimeframe}.`;
        recommendation.actionItems = [
          'Identify root cause of recurring issue',
          'Implement preventive controls',
          'Automate compliance checks',
          'Establish early warning system'
        ];
        recommendation.estimatedImpact = `Eliminate recurring violations`;
        recommendation.implementationTime = '4-8 weeks';
      }

      recommendations?.push(recommendation);
    });

    return recommendations;
  },

  /**
   * Calculate confidence score based on data volume
   */
  calculateConfidenceScore(dataPoints) {
    if (dataPoints >= 100) return 95;
    if (dataPoints >= 50) return 85;
    if (dataPoints >= 25) return 75;
    if (dataPoints >= 10) return 65;
    return 50;
  },

  /**
   * Calculate average time between violations
   */
  calculateAvgTimeBetween(violations) {
    if (violations?.length < 2) return 0;

    const dates = violations
      ?.map(v => new Date(v?.identified_at))
      ?.sort((a, b) => a - b);

    let totalDays = 0;
    for (let i = 1; i < dates?.length; i++) {
      const diffTime = Math.abs(dates?.[i] - dates?.[i - 1]);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      totalDays += diffDays;
    }

    return Math.round(totalDays / (dates?.length - 1));
  },

  /**
   * Forecast compliance trends for next period
   */
  async forecastComplianceTrends(months = 3) {
    try {
      const { data: violations, error } = await supabase
        ?.from('compliance_violations')
        ?.select('identified_at, severity')
        ?.order('identified_at', { ascending: true });

      if (error) throw error;

      // Group by month
      const monthlyData = {};
      violations?.forEach(v => {
        const month = v?.identified_at?.substring(0, 7);
        if (!monthlyData?.[month]) {
          monthlyData[month] = { total: 0, critical: 0, high: 0, medium: 0, low: 0 };
        }
        monthlyData[month].total += 1;
        monthlyData[month][v?.severity] += 1;
      });

      // Calculate trend
      const monthKeys = Object.keys(monthlyData)?.sort();
      const recentMonths = monthKeys?.slice(-6);
      const avgGrowth = this.calculateGrowthRate(recentMonths?.map(m => monthlyData?.[m]?.total));

      // Generate forecast
      const forecast = [];
      const lastMonth = monthlyData?.[recentMonths?.[recentMonths?.length - 1]];
      let projectedTotal = lastMonth?.total || 0;

      for (let i = 1; i <= months; i++) {
        projectedTotal = Math.round(projectedTotal * (1 + avgGrowth / 100));
        const forecastDate = new Date();
        forecastDate?.setMonth(forecastDate?.getMonth() + i);

        forecast?.push({
          month: forecastDate?.toISOString()?.substring(0, 7),
          projectedViolations: projectedTotal,
          trend: avgGrowth > 0 ? 'increasing' : avgGrowth < 0 ? 'decreasing' : 'stable',
          confidence: this.calculateConfidenceScore(violations?.length)
        });
      }

      return {
        historical: monthlyData,
        forecast,
        growthRate: avgGrowth?.toFixed(1),
        trend: avgGrowth > 5 ? 'concerning' : avgGrowth > 0 ? 'stable' : 'improving'
      };
    } catch (error) {
      console.error('Error forecasting compliance trends:', error);
      throw error;
    }
  },

  /**
   * Calculate growth rate from time series data
   */
  calculateGrowthRate(values) {
    if (values?.length < 2) return 0;

    let totalGrowth = 0;
    for (let i = 1; i < values?.length; i++) {
      if (values?.[i - 1] > 0) {
        const growth = ((values?.[i] - values?.[i - 1]) / values?.[i - 1]) * 100;
        totalGrowth += growth;
      }
    }

    return totalGrowth / (values?.length - 1);
  },

  /**
   * Run complete AI compliance analysis
   * Combines risk prediction, trend forecasting, and recommendations
   */
  async runCompleteAnalysis(filters = {}) {
    try {
      // Run all analysis components in parallel
      const [riskAnalysis, trendForecast] = await Promise.all([
        this.predictComplianceRisks(filters),
        this.forecastComplianceTrends(3)
      ]);

      // Fetch current violations for summary
      const { data: currentViolations, error } = await supabase
        ?.from('compliance_violations')
        ?.select('*')
        ?.eq('status', 'open')
        ?.order('severity', { ascending: false });

      if (error) throw error;

      // Calculate overall risk score
      const highRiskPredictions = riskAnalysis?.predictions?.filter(p => p?.riskLevel === 'high')?.length || 0;
      const totalPredictions = riskAnalysis?.predictions?.length || 1;
      const riskScore = Math.round((highRiskPredictions / totalPredictions) * 100);

      return {
        summary: {
          overallRiskScore: riskScore,
          riskLevel: riskScore > 70 ? 'high' : riskScore > 40 ? 'medium' : 'low',
          currentViolations: currentViolations?.length || 0,
          criticalViolations: currentViolations?.filter(v => v?.severity === 'critical')?.length || 0,
          predictedViolations: riskAnalysis?.predictions?.length || 0,
          analysisDate: new Date()?.toISOString()
        },
        riskAnalysis: {
          predictions: riskAnalysis?.predictions || [],
          recommendations: riskAnalysis?.recommendations || [],
          confidenceScore: riskAnalysis?.confidenceScore || 0
        },
        trendForecast: {
          forecast: trendForecast?.forecast || [],
          growthRate: trendForecast?.growthRate || '0',
          trend: trendForecast?.trend || 'stable',
          historical: trendForecast?.historical || {}
        },
        actionItems: this.generateActionItems(riskAnalysis, currentViolations)
      };
    } catch (error) {
      console.error('Error running complete analysis:', error);
      throw error;
    }
  },

  /**
   * Generate prioritized action items from analysis
   */
  generateActionItems(riskAnalysis, currentViolations) {
    const actionItems = [];

    // Add immediate actions for critical violations
    const criticalViolations = currentViolations?.filter(v => v?.severity === 'critical') || [];
    if (criticalViolations?.length > 0) {
      actionItems?.push({
        id: 'action-1',
        priority: 'immediate',
        title: 'Address Critical Violations',
        description: `${criticalViolations?.length} critical violations require immediate attention`,
        dueDate: new Date(Date.now() + 24 * 60 * 60 * 1000)?.toISOString(),
        category: 'remediation'
      });
    }

    // Add high-priority recommendations
    const highPriorityRecs = riskAnalysis?.recommendations?.filter(r => r?.priority === 'immediate') || [];
    highPriorityRecs?.slice(0, 3)?.forEach((rec, index) => {
      actionItems?.push({
        id: `action-${actionItems?.length + 1}`,
        priority: rec?.priority,
        title: rec?.title,
        description: rec?.description,
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString(),
        category: 'prevention'
      });
    });

    // Add recurring pattern actions
    const recurringPredictions = riskAnalysis?.predictions?.filter(p => p?.type === 'recurring_pattern') || [];
    if (recurringPredictions?.length > 0) {
      actionItems?.push({
        id: `action-${actionItems?.length + 1}`,
        priority: 'high',
        title: 'Break Recurring Violation Patterns',
        description: `${recurringPredictions?.length} recurring patterns detected that need systematic resolution`,
        dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)?.toISOString(),
        category: 'systematic'
      });
    }

    return actionItems;
  }
};

export default aiComplianceService;